import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RotateCcw, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Pill } from '../../components/ui/Pill';
import { AdminShell } from '../../components/AdminShell';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { useToast } from '../../components/ui/Toast';

interface QueueStat {
  name: string;
  waiting: number;
  active: number;
  completed: number;
  failed: number;
  delayed: number;
}

interface FailedJob {
  id: string;
  queue: string;
  name: string;
  failedReason: string;
  attemptsMade: number;
  timestamp: number;
}

/**
 * Admin → BullMQ jobs. Live stats per queue + a failed-jobs inspector so
 * we can debug stuck OCR / agent / notification work without an
 * SSH session.
 */
export function AdminJobsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();

  const stats = useQuery<QueueStat[]>({
    queryKey: ['admin-queues'],
    queryFn: async () => (await api.get('/admin/queues')).data,
    refetchInterval: 5_000,
  });
  const failed = useQuery<FailedJob[]>({
    queryKey: ['admin-queues-failed'],
    queryFn: async () => (await api.get('/admin/queues/failed')).data,
    refetchInterval: 15_000,
  });

  const retry = useMutation({
    mutationFn: async (input: { queue: string; id: string }) =>
      (await api.post(`/admin/queues/${input.queue}/jobs/${input.id}/retry`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-queues-failed'] });
      qc.invalidateQueries({ queryKey: ['admin-queues'] });
    },
    onError: () => toast.error(t('admin.jobs.retryError')),
  });

  const drop = useMutation({
    mutationFn: async (input: { queue: string; id: string }) =>
      (await api.delete(`/admin/queues/${input.queue}/jobs/${input.id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-queues-failed'] }),
    onError: () => toast.error(t('admin.jobs.dropError')),
  });

  async function onDrop(j: FailedJob) {
    const ok = await confirm({
      title: t('admin.jobs.dropConfirmTitle'),
      body: t('admin.jobs.dropConfirmBody', { name: j.name }),
      destructive: true,
      confirmLabel: t('common.delete'),
    });
    if (!ok) return;
    drop.mutate({ queue: j.queue, id: j.id });
  }

  return (
    <AdminShell>
      <div className="px-8 py-7 max-w-7xl w-full mx-auto">
        <div className="text-[11px] uppercase tracking-[0.18em] text-ink-500">{t('admin.eyebrow')}</div>
        <h1 className="display text-3xl font-semibold tracking-tight mt-1">{t('admin.jobs.title')}</h1>
        <p className="text-sm text-ink-600 dark:text-ink-300 mt-1">{t('admin.jobs.subtitle')}</p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
          {stats.isLoading ? (
            <div className="text-sm text-ink-500">{t('common.loading')}</div>
          ) : (stats.data ?? []).map((s) => (
            <Card key={s.name} className="p-5">
              <div className="flex items-center justify-between">
                <div className="font-medium">{s.name}</div>
                {s.failed > 0 ? <Pill tone="warn">{t('admin.jobs.hasFailures')}</Pill> : <Pill tone="success">{t('admin.jobs.healthy')}</Pill>}
              </div>
              <div className="mt-3 grid grid-cols-5 gap-2 text-xs text-ink-600 dark:text-ink-300">
                <Stat label={t('admin.jobs.stat.waiting')}   value={s.waiting} />
                <Stat label={t('admin.jobs.stat.active')}    value={s.active} />
                <Stat label={t('admin.jobs.stat.completed')} value={s.completed} />
                <Stat label={t('admin.jobs.stat.failed')}    value={s.failed} />
                <Stat label={t('admin.jobs.stat.delayed')}   value={s.delayed} />
              </div>
            </Card>
          ))}
        </div>

        <h2 className="display text-xl font-semibold mt-10">{t('admin.jobs.failedTitle')}</h2>
        <Card className="mt-3 overflow-hidden">
          {failed.isLoading ? (
            <div className="p-8 text-center text-ink-500">{t('common.loading')}</div>
          ) : !failed.data || failed.data.length === 0 ? (
            <div className="p-8 text-center text-ink-500 text-sm">{t('admin.jobs.failedEmpty')}</div>
          ) : (
            <ul className="divide-y divide-ink-200 dark:divide-ink-800">
              {failed.data.map((j) => (
                <li key={`${j.queue}:${j.id}`} className="px-5 py-3 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm">
                      <Pill tone="muted">{j.queue}</Pill>
                      <span className="font-medium truncate">{j.name}</span>
                      <span className="text-xs text-ink-500">· {t('admin.jobs.attempts', { n: j.attemptsMade })}</span>
                    </div>
                    <pre className="text-xs text-warn-700 dark:text-warn-300 mt-1 whitespace-pre-wrap font-mono">{j.failedReason}</pre>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="secondary" size="sm" onClick={() => retry.mutate({ queue: j.queue, id: j.id })}>
                      <RotateCcw size={14} /> {t('admin.jobs.retry')}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => onDrop(j)}>
                      <Trash2 size={14} /> {t('common.delete')}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </AdminShell>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.12em] text-ink-500">{label}</div>
      <div className="num text-base font-semibold text-ink-900 dark:text-ink-100">{value}</div>
    </div>
  );
}
